import { Bounds } from '@ardoq/graph';
import { createRouter } from './router';
import {
  BlocksGraph,
  BlocksNode,
  BlocksLinksAggregation,
  BlocksPlacementStyle,
} from '../types';

import {
  getNodeMinimumCellHeight,
  getNodeMinimumCellWidth,
  setNodeCellRect as setNodeCellBounds,
  getWrappedNodeLabels,
} from '../visual/node';
import { setReferenceRoute } from '../visual/reference';
import { getGetBusId } from './getbusid';
import {
  isGroup,
  isNodeOpen,
  isNodeRoot,
  getVisibleDescendants,
} from '../util';
import { getOpenHeaderHeight } from '../visual/node.composition';
import {
  SpanConstraint,
  minimumSpanSizes,
} from 'tabview/visualizations/aq-blocks/placeandroute/spansizes';
import { getColPositions, getRowPositions } from './util';

const partialSum = (array: number[], first: number, last: number) => {
  let ret = 0;

  for (let i = first; i <= last; ++i) {
    ret += array[i];
  }

  return ret;
};

export const placeAndRoute = (
  graph: BlocksGraph,
  linksAggregation: BlocksLinksAggregation,
  placementStyle: BlocksPlacementStyle
): void => {
  const getBusId = getGetBusId(graph, linksAggregation);

  /* get ready for routing */
  const resetGridArrays = (graph: BlocksGraph) => {
    const root = graph.root;
    let colCnt = 0;
    let rowCnt = 0;

    const countit = (node: BlocksNode) => {
      if (node.layoutData) {
        const { col, colSpan, row, rowSpan } = node.layoutData;

        colCnt = Math.max(colCnt, col + colSpan + 1);
        rowCnt = Math.max(rowCnt, row + rowSpan + 1);
      }

      if (node.children) {
        for (const child of node.children) {
          countit(child);
        }
      }
    };

    countit(root);

    root.layoutData = {
      col: 0,
      colSpan: colCnt,
      row: 0,
      rowSpan: rowCnt,
    };

    root.colSize = new Array<number>(colCnt).fill(0);
    root.rowSize = new Array<number>(rowCnt).fill(0);
  };

  const buildMarginConstraints = (
    graph: BlocksGraph,
    dimension: number,
    constraints: SpanConstraint[]
  ) => {
    const margin = placementStyle.routingChannelMinimumSize;

    const isEmptyColumn = (_col: number, _row: number, _rowSpan: number) => {
      return false;
    };

    const isEmptyRow = (_row: number, _col: number, _colSpan: number) => {
      return false;
    };

    for (const node of getVisibleDescendants(graph.root)) {
      if (!isNodeRoot(node) && node.layoutData) {
        const { col, colSpan, row, rowSpan } = node.layoutData;

        switch (dimension) {
          case 0:
            if (col > 0 && !isEmptyColumn(col - 2, row, rowSpan)) {
              constraints.push({
                first: col - 1,
                last: col - 1,
                minimum: margin,
              });
            }

            if (!isEmptyColumn(col + colSpan + 1, row, rowSpan)) {
              constraints.push({
                first: col + colSpan,
                last: col + colSpan,
                minimum: margin,
              });
            }

            break;

          case 1:
            if (row > 0 && !isEmptyRow(row - 2, col, colSpan)) {
              constraints.push({
                first: row - 1,
                last: row - 1,
                minimum: margin,
              });
            }

            if (!isEmptyRow(row + rowSpan + 1, col, colSpan)) {
              constraints.push({
                first: row + rowSpan,
                last: row + rowSpan,
                minimum: margin,
              });
            }

            break;
        }
      }
    }
  };

  const buildNodeConstraints = (
    graph: BlocksGraph,
    dimension: number,
    constraints: SpanConstraint[]
  ) => {
    const { root } = graph;
    const { colSize, rowSize } = root;

    if (!colSize || !rowSize) {
      return;
    }

    const doit = (node: BlocksNode) => {
      if (isGroup(node) && isNodeOpen(node)) {
        for (const child of node.children) {
          doit(child);
        }
      }

      if (node.layoutData) {
        const { col, colSpan, row, rowSpan } = node.layoutData;

        switch (dimension) {
          case 0:
            {
              const minimum = getNodeMinimumCellWidth(node, placementStyle);
              constraints.push({
                first: col,
                last: col + colSpan - 1,
                minimum: minimum,
              });
            }

            break;

          case 1:
            if (isGroup(node) && isNodeOpen(node)) {
              const minimum = getOpenHeaderHeight(node);
              constraints.push({ first: row, last: row, minimum: minimum });
            } else {
              const minimum = getNodeMinimumCellHeight(
                node,
                partialSum(colSize, col, col + colSpan - 1),
                placementStyle
              );
              constraints.push({
                first: row,
                last: row + rowSpan - 1,
                minimum: minimum,
              });
            }

            break;
        }
      }
    };

    if (graph.root.children) {
      for (const child of graph.root.children) {
        doit(child);
      }
    }
  };

  const placeNodes = (graph: BlocksGraph) => {
    if (!graph.root.colSize || !graph.root.rowSize) {
      return;
    }

    graph.root.content = [0, 0, 1, 1];

    const colPos = getColPositions(graph.root);
    const rowPos = getRowPositions(graph.root);

    const doit = (node: BlocksNode) => {
      if (node.layoutData) {
        const { col, colSpan, row, rowSpan } = node.layoutData;
        const cell: Bounds = [
          colPos[col],
          rowPos[row],
          colPos[col + colSpan],
          rowPos[row + rowSpan],
        ];

        setNodeCellBounds(node, cell);
      }

      if (isGroup(node) && isNodeOpen(node)) {
        for (const child of node.children) {
          doit(child);
        }
      }
    };

    doit(graph.root);
  };

  /* get ready for laying out */

  resetGridArrays(graph);

  /* do routing as appropriate */

  const router = createRouter(graph, getBusId, placementStyle);
  const { root } = graph;

  if (linksAggregation !== 'hide') {
    for (const reference of graph.edges) {
      router.calculateWaypoints(reference);
    }
  }

  if (root.colSize) {
    /* build and solve horizontal constraints */
    const constraints: SpanConstraint[] = [];

    buildNodeConstraints(graph, 0, constraints);
    buildMarginConstraints(graph, 0, constraints);
    router.buildGridConstraints(graph, 0, constraints);
    root.colSize = minimumSpanSizes(root.colSize.length, constraints);
  }

  if (root.colSize) {
    /* wrap node labels */
    for (const node of getVisibleDescendants(root)) {
      if (!node.layoutData || !node.parent) {
        continue;
      }

      const { col, colSpan } = node.layoutData;
      const width = partialSum(root.colSize, col, col + colSpan - 1);

      node.displayLabels = getWrappedNodeLabels(node, width);
    }
  }

  if (root.rowSize) {
    /* build and solve vertical constraints */
    const constraints: SpanConstraint[] = [];

    buildNodeConstraints(graph, 1, constraints);
    buildMarginConstraints(graph, 1, constraints);
    router.buildGridConstraints(graph, 1, constraints);
    root.rowSize = minimumSpanSizes(root.rowSize.length, constraints);
  }

  /* assign sizes and positions to nodes */

  placeNodes(graph); // set node positions according to their layout

  /* assign sizes and positions to references */

  for (const link of graph.edges) {
    const route = router.calculateRoute(link);

    if (route && route.length > 1) {
      setReferenceRoute(link, route);
    } else {
      link.route = undefined;
      link.sourceProxy = undefined;
      link.targetProxy = undefined;
      link.bounds = undefined;
    }
  }

  /* get set of plausible label positions (rectangles) for each reference */

  // each link gets a vector of plausible positions

  for (const link of graph.edges) {
    link.labelSegment = undefined;

    if (!link.route) {
      continue;
    }

    for (let i = 1; i < link.route.length; ++i) {
      // if segement [i-1 to i] is long enough for a label
      //   add it to the plausible set for this reference
    }
  }

  /* choose individual reference label position to minimise global overlap */
};
